import axios from 'axios';
import appConfig from '../config/appConfig';

const API_BASE_URL = `${appConfig.apiBase}`;

/**
 * Revenue Service - Handles revenue-related API calls for clinic revenue dashboard
 */

/**
 * Get revenue overview with summary cards and breakdowns
 */
export const getRevenueOverview = async (filterType, doctorId, startDate, endDate) => {
  try {
    const params = { filterType };
    if (doctorId) params.doctorId = doctorId;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const clinicId = (typeof window !== 'undefined' && window.__CLINIC_ID__) || null;
    const response = await axios.get(`${API_BASE_URL}/clinic/revenue/overview`, {
      params,
      headers: { 'X-Clinic-Id': clinicId }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching revenue overview:', error);
    return {};
  }
};

/**
 * Get pending payments with pagination and search
 */
export const getPendingPayments = async (limit = 20, offset = 0, startDate, endDate, searchQuery) => {
  try {
    const params = { limit, offset };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (searchQuery) params.search = searchQuery;

    const clinicId = (typeof window !== 'undefined' && window.__CLINIC_ID__) || null;
    const response = await axios.get(`${API_BASE_URL}/clinic/revenue/pending-payments`, {
      params,
      headers: { 'X-Clinic-Id': clinicId }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching pending payments:', error);
    return { pending: [], total: 0, totalPendingAmount: 0 };
  }
};

/**
 * Get doctor-wise revenue breakdown
 */
export const getDoctorBreakdown = async (filterType, startDate, endDate) => {
  try {
    const params = { filterType };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const clinicId = (typeof window !== 'undefined' && window.__CLINIC_ID__) || null;
    const response = await axios.get(`${API_BASE_URL}/clinic/revenue/doctor-breakdown`, {
      params,
      headers: { 'X-Clinic-Id': clinicId }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching doctor breakdown:', error);
    return [];
  }
};

/**
 * Get revenue trend data for charts
 */
export const getRevenueTrend = async (days = 7) => {
  try {
    const clinicId = (typeof window !== 'undefined' && window.__CLINIC_ID__) || null;
    const response = await axios.get(`${API_BASE_URL}/clinic/revenue/trend`, {
      params: { days },
      headers: { 'X-Clinic-Id': clinicId }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching revenue trend:', error);
    return [];
  }
};

/**
 * Export revenue report (CSV/PDF)
 */
export const exportReport = async (format, period) => {
  try {
    const params = { format };
    if (typeof period === 'string') {
      params.month = period;
    } else if (period && typeof period === 'object') {
      if (period.month) params.month = period.month;
      if (period.startDate) params.startDate = period.startDate;
      if (period.endDate) params.endDate = period.endDate;
    }

    const clinicId = (typeof window !== 'undefined' && window.__CLINIC_ID__) || null;
    const response = await axios.get(`${API_BASE_URL}/clinic/revenue/export`, {
      params,
      headers: { 'X-Clinic-Id': clinicId },
      responseType: 'blob'
    });

    // Download file
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `revenue-report.${format}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error) {
    console.error('Error exporting report:', error);
    throw error;
  }
};

/**
 * Legacy methods for backward compatibility
 */
export const getRevenueSummary = async (clinicId) => {
  // Redirect to new overview endpoint
  return getRevenueOverview('month', null, null, null);
};

export const getRevenueDetails = async (clinicId, startDate, endDate) => {
  return getRevenueOverview('customRange', null, startDate, endDate);
};

export const getRevenueByDoctor = async (clinicId) => {
  return getDoctorBreakdown('month', null, null);
};

/**
 * Get monthly summary
 */
export const getMonthlySummary = async (monthStr) => {
  try {
    const clinicId = (typeof window !== 'undefined' && window.__CLINIC_ID__) || null;
    const response = await axios.get(`${API_BASE_URL}/clinic/revenue/monthly-summary`, {
      params: { month: monthStr },
      headers: { 'X-Clinic-Id': clinicId }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching monthly summary:', error);
    return {};
  }
};

// ========== SUBSCRIPTION REVENUE (APP OWNER) ==========

/**
 * Get subscription revenue overview for app owner
 */
export const getSubscriptionRevenueOverview = async (startDate, endDate) => {
  try {
    const params = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const response = await axios.get(`${API_BASE_URL}/owner/revenue/subscription/overview`, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching subscription revenue overview:', error);
    return {};
  }
};

/**
 * Get subscription revenue trend
 */
export const getSubscriptionRevenueTrend = async (days = 7) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/owner/revenue/subscription/trend`, {
      params: { days }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching subscription revenue trend:', error);
    return [];
  }
};

/**
 * Get subscription revenue monthly summary
 */
export const getSubscriptionMonthlySummary = async (month) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/owner/revenue/subscription/monthly-summary`, {
      params: { month }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching subscription monthly summary:', error);
    return {};
  }
};

/**
 * Get subscription revenue by clinic
 */
export const getSubscriptionRevenueByClinic = async (startDate, endDate) => {
  try {
    const params = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const response = await axios.get(`${API_BASE_URL}/owner/revenue/subscription/by-clinic`, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching subscription revenue by clinic:', error);
    return [];
  }
};

/**
 * Get subscription revenue by plan
 */
export const getSubscriptionRevenueByPlan = async (startDate, endDate) => {
  try {
    const params = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const response = await axios.get(`${API_BASE_URL}/owner/revenue/subscription/by-plan`, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching subscription revenue by plan:', error);
    return [];
  }
};

/**
 * Get pending subscription invoices
 */
export const getPendingSubscriptionInvoices = async (limit = 20, offset = 0, startDate, endDate, searchQuery) => {
  try {
    const params = { limit, offset };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (searchQuery) params.search = searchQuery;

    const response = await axios.get(`${API_BASE_URL}/owner/revenue/subscription/pending-invoices`, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching pending subscription invoices:', error);
    return { pending: [], total: 0, totalPendingAmount: 0 };
  }
};
